import { EmployerKeysToManage } from '@/people/state/types/employer';
import { SurveyTemplate } from '@/surveys/state';

const importedProfileFieldKeys: { [key in keyof EmployerKeysToManage]: string } = {
  firstName: 'Nome',
  lastName: 'Cognome',
  email: 'Email',
  birthDate: 'Data di nascita',
  role: 'Ruolo aziendale',
  department: 'Dipartimento',
  fiscalCode: 'Codice Fiscale',
  jobLevel: 'Job Level',
  emailManager: 'Email del Responsabile',
  area: 'Area (team)',
};

export const it = {
  configuration: {
    macroStepTitles: {
      addDelegate: 'Aggiungi Delegato',
      customize: 'Personalizza',
      companyData: 'Anagrafiche',
    },
    hr: {
      start: {
        title: 'Ciao {{name}}, ti diamo il benvenuto su Slash',
        subtitle:
          'Configura la piattaforma in pochi semplici step per cominciare a sfruttarne tutte le potenzialità. Se salti, non ti verrà più riproposta.',
        startConfiguration: 'Iniziamo',
        skipConfiguration: 'Salta configurazione',
        verticalStepTitle: 'STEP',
        setup: {
          step1: {
            title: 'Aggiungi delegato',
            description:
              'Aggiungi un delegato con i tuoi stessi permessi per una gestione condivisa',
          },
          step2: {
            title: 'Personalizza',
            description:
              'Personalizza la piattaforma Slash caricando il sito web o il logo della tua azienda',
          },
          step3: {
            title: 'Carica anagrafiche',
            description:
              'Carica le anagrafiche dei dipendenti indicando nome, cognome, email per ogni persona. Non hai un file simile? {{downloadFile}}',
            downloadFileLabel: 'Scarica il template',
          },
        },
      },
      delegate: {
        add: {
          title: 'Aggiungi delegato',
          subtitle:
            'Assegna ad uno o più delegati i tuoi stessi permessi per una gestione condivisa. Inserisci i dati correttamente e clicca su aggiungi. I delegati riceveranno un invito istantaneamente. Potrai sempre aggiungere altri delegati dalla sezione Organizzazione.',
          addDelegate: {
            firstName: 'Nome del Delegato',
            lastName: 'Cognome del Delegato',
            email: 'Email del Delegato',
            cta: {
              skip: 'Salta questo step',
              next: 'Aggiungi',
              add: 'Aggiungi',
            },
          },
        },
        confirm: {
          title: 'Aggiungi delegato',
          subtitle:
            'Assegna ad uno o più delegati i tuoi stessi permessi per una gestione condivisa. Inserisci i dati correttamente e clicca su aggiungi. I delegati riceveranno un invito istantaneamente. Potrai sempre aggiungere altri delegati dalla sezione Organizzazione.',
          addDelegate: {
            firstName: 'Nome del Delegato',
            lastName: 'Cognome del Delegato',
            email: 'Email del Delegato',
            firstNamePlaceholder: 'es: Mario',
            lastNamePlaceholder: 'es: Rossi',
            emailPlaceholder: 'es: mariorossi@dominio.it',
            cta: {
              skip: 'Sì, aggiungi',
              next: 'No, vai avanti',
              add: 'Aggiungi',
            },
            alertInvalidEmail: "Il formato dell'email non è valido. Controlla e riprova",
            infoBox: 'Per maggiori informazioni, consulta la !{DOCS}! o !{CONTACT}!',
            labelDocs: 'guida',
            labelContact: 'contattaci',
          },
          currentDelegates: {
            title: 'Delegato',
            firstNameAndLastName: 'Nome e Cognome',
            email: 'Email',
            deleted: 'Delegato rimosso con successo',
          },
        },
      },
      setLogo: {
        title: 'Personalizza la piattaforma con il tuo logo',
        subtitle:
          'Carica il logo della tua azienda per rendere Slash ancora più tuo. Inserisci il sito aziendale o carica i loghi manualmente.',
        siteLabel: 'Sito web della tua azienda',
        sitePlaceholder: 'https://',
        siteDescription:
          '{{remainingDesignQuota}}/{{totalDesignQuota}} prove disponibili per caricare il logo partendo dalla pagina web inserita',
        alert:
          "Per un'esperienza ottimale, ti consigliamo di caricare il logo in entrambe le versioni: chiara (light) e scura (dark).\nSe ne hai caricato solo una, genereremo automaticamente la versione mancante con l’AI. Potrai sempre modificarla nella sezione <i>Impostazioni</i> della piattaforma.",
        remainingDesignQuotaZeroAlert:
          'Hai esaurito le prove disponibili per caricare il logo partendo dalla pagina web inserita. Puoi comunque caricare i loghi manualmente e scegliere il colore del tema.',
        alertError:
          'Non è stato possibile recuperare i loghi dall’URL inserita. Prova con un link diverso oppure caricali manualmente.',
        cta: {
          generate: 'Genera',
        },
        loading: {
          title: 'L’AI sta analizzando la pagina inserita',
          description: 'I loghi verranno presto visualizzati in anteprima in pagina',
        },
        uploadLogo: {
          titleWhiteMode: 'Logo aziendale White mode',
          titleTooltipWhiteMode: 'Versione scura del logo, ideale per sfondi chiari.',
          titleDarkMode: 'Logo aziendale Dark mode',
          titleTooltipDarkMode: 'Versione chiara del logo, ideale per sfondi scuri o colorati.',
        },
      },
      setColor: {
        title: 'Scegli il colore della piattaforma',
        subtitle:
          'Personalizza la piattaforma con uno dei colori a disposizione. Potrai cambiare questo aspetto in Impostazioni',
        titleColor: 'Colore tema',
        previewLabel: 'Anteprima piattaforma',
        previewLabelTooltip:
          'Qui puoi vedere un’anteprima della piattaforma in modalità dark e light. La scelta del tema è personale e può essere gestita da ciascun utente direttamente in piattaforma dal dropdown in alto a destra.',
      },
      setData: {
        title: 'Importa l’anagrafica aziendale',
        subtitle:
          'Carica i dati dei tuoi dipendenti per creare i loro profili. Assicurati che ogni profilo contenga i seguenti campi: Nome, Cognome, Email.',
        warning:
          'Puoi anche aggiungere i seguenti dati facoltativi per rendere le analisi più accurate: Data di nascita (gg-mm-aaaa), Ruolo aziendale, Dipartimento, Responsabile (nome, cognome, mail), Team, Codice fiscale, Job level.',
        downloadLabel: 'Scarica il nostro template',
        downloadFile:
          'Non hai un file simile?\n {{downloadFile}} e compilalo con i dati dei tuoi dipendenti',
        uploadedFiles: 'Documento caricato',
        loadingFile: {
          title: 'L’AI sta verificando i dati caricati',
          description: 'Le anagrafiche verranno presto visualizzate nella piattaforma',
        },
        infoBox:
          'Per informazioni o ricevere supporto nel caricamento, consulta la !{DOCS}! o !{CONTACT}!',
        labelDocs: 'guida',
        labelContact: 'contattaci',
        warningSaveBeforeQuit:
          '<b>Attenzione</b> Se esci senza avere completato la revisione e correzione del anagrafiche, il file caricato non verrà salvato. Assicurati di completare l’operazione prima di uscire.',
      },
      verifyData: {
        title: 'Verifica anagrafiche',
        subtitle:
          'Ecco il recap delle anagrafiche caricate. Correggi i dati mancanti per permettere a tutti di ricevere l’assessment e accedere alla piattaforma. Se non puoi correggere ora, conferma e procedi in seguito nella sezione Anagrafiche.',
        charts: {
          totalProfiles: 'Totale Anagrafiche',
          completeProfiles: 'Profili Completi',
          incompleteProfiles: 'Profili Incompleti',
          profilesLabel: 'Profili',
        },
        previewDataWarning: {
          title: 'Verifica Dati',
          incompleteData: {
            message: {
              singular: 'Dato assente o incompleto in 1 profilo',
              plural: 'Dato assente o incompleto in {{count}} profili',
            },
            fieldKeys: {
              ...importedProfileFieldKeys,
            },
          },
        },
        dialogConfirmNavigateBack: {
          title: 'Anagrafiche non confermate',
          description: 'Tornando indietro, le informazioni inserite andranno perse.',
          explicitQuestion: 'Sei sicuro di voler tornare indietro?',
          cancel: 'Annulla',
          confirm: 'Sì, torna indietro',
        },
        dialogEditImportedProfiles: {
          title: 'Elenco anagrafiche',
          description:
            'Qui trovi l’elenco delle anagrafiche che presentano problemi. Puoi modificarle o completarle direttamente da questa schermata o, in alternativa, in un secondo momento dalla sezione Anagrafiche della piattaforma. Tutte le modifiche vengono salvate automaticamente in tempo reale, anche chiudendo la finestra, i dati aggiornati resteranno salvati.',
          cta: {
            confirm: 'Conferma anagrafiche',
            showData: 'Visualizza profili',
          },
          table: {
            name: 'Nome',
            surname: 'Cognome',
            email: 'Email',
            birthDate: 'Data di nascita',
            role: 'Ruolo aziendale',
            department: 'Dipartimento',
            manager: 'Email responsabile',
            area: 'Area (team)',
            fiscalCode: 'Codice Fiscale',
            jobLevel: 'Job Level',
            filters: {
              title: 'Filtri',
              cta: {
                close: 'Chiudi',
              },
            },
          },
          filters: {
            importedProfileCompleted: 'Profili Completi',
            importedProfileIncompleted: 'Profili Incompleti',
            importedProfileAll: 'Tutti i Profili',
            cta: {
              submit: 'Applica',
              reset: 'Reset',
            },
          },
        },
      },
      typ: {
        configurationCompletedThankYouPage: {
          title: 'Configurazione completata!',
          subtitle:
            'La piattaforma è pronta! Inizia subito a coinvolgere i tuoi dipendenti e a valorizzare il loro potenziale.',
          cta: {
            next: 'Vai su Slash',
          },
        },
      },
    },
    learning: {
      start: {
        title: 'Ciao {{name}}, ti diamo il benvenuto su Slash',
        subtitle: "Conferma i tuoi dati e completa l'assessment per cominciare il tuo percorso!",
        startConfiguration: 'Iniziamo',
      },
      employeeConfirmData: {
        title: 'Controlla i tuoi dati anagrafici',
        subtitle:
          'Verifica che i tuoi dati anagrafici siano corretti. In caso di errori, contatta il tuo referente HR',
        disclaimer: 'Clicca su Avanti per confermare i tuoi dati',
        profile: {
          role: 'Ruolo',
          department: 'Dipartimento',
          manager: 'Responsabile',
          email: 'E-mail',
          birthDate: 'Data di nascita',
        },
      },
      employeeStartSurvey: {
        previousButton: 'Torna alla sezione precedente',
        title: "Compila l'assessment",
        subtitle:
          'Rispondi a poche semplici domande per aiutarci a delineare il tuo profilo e individuare le tue {{templateName}}',
        disclaimer: {
          [SurveyTemplate.SoftSkillAssessment]:
            'Ti serviranno circa 15 minuti per completarlo. Una volta iniziato, non sarà possibile metterlo in pausa: in caso di uscita anticipata, i dati non verranno salvati. Nel rispondere, pensa come ti comporteresti in situazioni quotidiane, non necessariamente legate all’ambito professionale.',
          [SurveyTemplate.HardSkillAssessment]:
            'Ti serviranno circa 15 minuti per completarlo. Una volta iniziato, non sarà possibile metterlo in pausa: in caso di uscita anticipata, i dati non verranno salvati. Nel rispondere fai riferimento alle tue conoscenze attuali e al modo in cui interpreteresti o affronteresti le situazioni proposte.',
        },
      },
      employeeCompleteSurvey: {
        title: "Completa l'assessment",
        subtitle: 'Partiamo da te, scopri il tuo profilo',
        stepXonY: '{{currentStep}} su {{totalSteps}} domande',
        exit: "Esci dall'assessment",
        confirmExitFromSurveyDialog: {
          title: "Esci dall'assessment",
          description:
            "Uscendo dall'assessment, avrai la possibilità di completarlo in un secondo momento, ricominciando da capo. Sei sicuro di voler uscire?",
          confirm: 'Esci',
          cancel: 'Annulla',
        },
      },
      typ: {
        configurationCompletedThankYouPage: {
          title: "Hai completato l'assessment con successo!",
          subtitle:
            'Le risposte sono state inviate correttamente.\nTorna in home page per visualizzare gli assessment disponibili e continuare ad esplorare la piattaforma.',
          cta: {
            next: 'Vai su Slash',
          },
        },
      },
    },
  },
};
